const APIHandler = (baseUrl: string) => {
  const BASE_API_URL = baseUrl;

  const getUserSpendAnalytics = async () => {
    return await fetch(`${BASE_API_URL}/analytics/user_spend`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  };

  const createProject = async (
    description: string,
    theme: string,
    siteBackground?: File | null,
    // Can be a single string like "*" or an array of references
    secretReferences: string | string[] = []
  ) => {
    const formData = new FormData();
    formData.append('description', description);
    formData.append('theme', theme);

    if (siteBackground) {
      formData.append('background_image', siteBackground);
    }

    // normalize to array
    const refs = Array.isArray(secretReferences) ? secretReferences : [secretReferences];
    for (const ref of refs) {
      formData.append('secret_references', ref);
    }

    return await fetch(`${BASE_API_URL}/create_project`, {
      method: 'POST',
      body: formData,
    });
  };

  const createBlankProject = async (
    description: string,
    theme: string,
    siteBackground?: File | null,
    // Can be a single string like "*" or an array of references
    secretReferences: string | string[] = []
  ) => {
    const formData = new FormData();
    formData.append('description', description);
    formData.append('theme', theme);

    if (siteBackground) {
      formData.append('background_image', siteBackground);
    }

    // normalize to array
    const refs = Array.isArray(secretReferences) ? secretReferences : [secretReferences];
    for (const ref of refs) {
      formData.append('secret_references', ref);
    }

    return await fetch(`${BASE_API_URL}/create_blank_project`, {
      method: 'POST',
      body: formData,
    });
  };

  const getProjects = async (include_published_info = false) => {
    let url = `${BASE_API_URL}/fetch_projects`;
    if (include_published_info) {
      url += '?include_published_info=true';
    }
    return await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  };

  const getProjectMetadata = async (projectName: string) => {
    return await fetch(`${BASE_API_URL}/project/${projectName}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  };

  const getSubdomains = async (projectName: string) => {
    return await fetch(`${BASE_API_URL}/subdomains?project_name=${projectName}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  };

  const allocateSubdomain = async (projectName: string, subdomain: string) => {
    const payload = {
      project_name: projectName,
      subdomain: subdomain,
    };

    return await fetch(`${BASE_API_URL}/allocate_subdomain`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  };

  const updateSubdomain = async (projectName: string, subdomain: string) => {
    const payload = {
      project_name: projectName,
      subdomain: subdomain,
    };

    return await fetch(`${BASE_API_URL}/update_subdomain`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  };

  const releaseSubdomain = async (projectName: string, subdomain: string) => {
    const payload = {
      project_name: projectName,
      subdomain: subdomain,
    };

    return await fetch(`${BASE_API_URL}/release_subdomain`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  };

  const tagCommit = async (projectName: string, commitHash: string, tagName: string) => {
    const payload = {
      project_name: projectName,
      commit_hash: commitHash,
      tag_name: tagName,
    };

    return await fetch(`${BASE_API_URL}/tag_project_commit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  };

  const publishProject = async (projectName: string, gitRef: string) => {
    const payload = {
      project_name: projectName,
      git_ref: gitRef,
    };

    return await fetch(`${BASE_API_URL}/publish_project`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  };

  const unpublishProject = async (projectName: string) => {
    const payload = {
      project_name: projectName,
    };

    return await fetch(`${BASE_API_URL}/unpublish_project`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  };

  const getComponentLibrary = async () => {
    //FIXME: this is hardcoded right now for only two namespaces
    return await fetch(`${BASE_API_URL}/library_components?namespaces=example`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  };

  const addProjectComponent = async (
    projectName: string,
    componentNamespace: string,
    componentName: string,
    layoutMetadata: any,
    pageId?: string | null,
    content?: string | null
  ) => {
    const request: any = {
      project_name: projectName,
      component_namespace: componentNamespace,
      component_name: componentName,
      layout_metadata: layoutMetadata,
      ...(!!pageId && { page_id: pageId }),
      ...(!!content && { content: content }),
    };

    return await fetch(`${BASE_API_URL}/add_project_component`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  };

  const addProjectChart = async (
    projectName: string,
    visualId: string,
    layoutMetadata: object,
    echartsConfig?: object,
    pageId?: string | null
  ) => {
    const request: any = {
      project_name: projectName,
      visual_id: visualId,
      layout_metadata: layoutMetadata,
      echarts_config: echartsConfig,
      ...(!!pageId && { page_id: pageId }),
    };
    return await fetch(`${BASE_API_URL}/add_project_chart`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  };

  const updateProjectComponentLayoutMetadata = async (
    projectName: string,
    componentId: string,
    layoutMetadata: any
  ) => {
    const request = {
      project_name: projectName,
      component_id: componentId,
      layout_metadata: layoutMetadata,
    };

    return await fetch(`${BASE_API_URL}/update_project_component_layout_metadata`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  };

  const updateProjectComponentSourceCode = async (
    projectName: string,
    componentId: string,
    sourceCode: string
  ) => {
    const request = {
      project_name: projectName,
      component_id: componentId,
      source_code: sourceCode,
    };

    return await fetch(`${BASE_API_URL}/update_project_component_source_code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  };

  const updateApplicationMetadata = async (
    projectName: string,
    applicationName: string,
    applicationDescription: string,
    applicationCategory: string
  ) => {
    const request = {
      project_name: projectName,
      application_name: applicationName,
      application_description: applicationDescription,
      application_category: applicationCategory,
    };

    return await fetch(`${BASE_API_URL}/update_application_metadata`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  };

  const updateApplicationSettings = async (
    projectName: string,
    applicationLoginEnabled: boolean | null = null,
    defaultDeploymentChainReference: string | null = null
  ) => {
    const request = {
      project_name: projectName,
      application_login_enabled: applicationLoginEnabled,
      default_deployment_chain_reference: defaultDeploymentChainReference,
    };

    return await fetch(`${BASE_API_URL}/update_application_settings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  };

  const deleteProjectFile = async (projectName: string, filePath: string) => {
    const request = {
      project_name: projectName,
      file_path: filePath,
    };
    return await fetch(`${BASE_API_URL}/delete_project_file`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  };

  const resetProjectFile = async (
    projectName: string,
    filePath: string,
    gitRef: string,
    prune?: boolean,
    extraMetadata?: string
  ) => {
    let request: any = {
      project_name: projectName,
      file_path: filePath,
      git_ref: gitRef,
    };
    if (prune) request.prune = prune;
    if (extraMetadata) request.extra_metadata = extraMetadata;

    return await fetch(`${BASE_API_URL}/reset_project_file`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  };

  // TODO: the endpoint is capable of batch deleting projects, but we don't
  //  currently have a use case (from the MI builder) to do that
  const deleteProject = async (projectName: string) => {
    const request = {
      projects: [projectName],
    };

    return await fetch(`${BASE_API_URL}/delete_projects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  };

  const requestErrorCorrection = async (
    projectName: string,
    componentId: string,
    errorMessage: string
  ) => {
    const request = {
      project_name: projectName,
      component_id: componentId,
      error_message: errorMessage,
    };

    return await fetch(`${BASE_API_URL}/error_correction`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  };

  /**
   * Uploads an image to a project using multipart/form-data
   * @param {FormData} formData - FormData containing project_name and upload_file
   * @returns {Promise<Response>} The server response
   */
  const addImage = async (formData: FormData): Promise<Response> => {
    return await fetch(`${BASE_API_URL}/add_image`, {
      method: 'POST',
      body: formData,
    });
  };

  /**
   * Edits an element in a project component
   * @param {Object} params - The edit element parameters
   * @param {string} params.projectName - The project name
   * @param {string} params.componentId - The component ID
   * @param {string} params.elementXPath - The element XPath
   * @param {Object | null} params.styleRequest - Style changes to apply
   * @param {string | null} params.textRequest - Text content changes
   * @param {string | null} params.backgroundImageRequest - Background image URL
   * @param {string} params.domTreeRepr - DOM tree representation
   * @returns {Promise<Response>} The server response
   */
  const editElement = async (params: {
    projectName: string;
    componentId: string;
    elementXPath: string;
    styleRequest?: object | null;
    textRequest?: string | null;
    backgroundImageRequest?: string | null;
    domTreeRepr: string;
  }): Promise<Response> => {
    const request = {
      project_name: params.projectName,
      component_id: params.componentId,
      element_xpath: params.elementXPath,
      style_request: params.styleRequest || null,
      text_request: params.textRequest || null,
      background_image_request: params.backgroundImageRequest || null,
      dom_tree_repr: params.domTreeRepr,
    };

    return await fetch(`${BASE_API_URL}/edit_element`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  };

  // Pages
  const addPage = async (
    projectName: string,
    name: string,
    path: string,
    components?: Record<string, Record<string, number>>
  ) => {
    const request = {
      project_name: projectName,
      name,
      path,
      components,
    };

    return await fetch(`${BASE_API_URL}/add_page`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  };

  const editPage = async (
    projectName: string,
    pageId: string,
    newName?: string | null,
    newPath?: string | null
  ) => {
    const request: any = {
      project_name: projectName,
      page_id: pageId,
      new_name: newName ?? null,
      new_path: newPath ?? null,
    };

    return await fetch(`${BASE_API_URL}/edit_page`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  };

  const deletePage = async (projectName: string, pageId: string) => {
    const request = {
      project_name: projectName,
      page_id: pageId,
    };

    return await fetch(`${BASE_API_URL}/delete_page`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  };

  const editPageLayout = async (projectName: string, pageId: string, layoutMetadata: object) => {
    const request = {
      project_name: projectName,
      page_id: pageId,
      layout_metadata: layoutMetadata,
    };

    return await fetch(`${BASE_API_URL}/edit_page_layout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  };

  const removePageComponent = async (projectName: string, pageId: string, componentId: string) => {
    const request = {
      project_name: projectName,
      page_id: pageId,
      component_id: componentId,
    };

    return await fetch(`${BASE_API_URL}/remove_page_component`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  };

  // Analytics: sum of creator points, optionally filtered by project_name
  const getCreatorPointsSum = async (projectName?: string | null) => {
    let url = `${BASE_API_URL}/analytics/creator_points/sum`;
    if (projectName) {
      url += `?project_name=${encodeURIComponent(projectName)}`;
    }
    return await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  };

  // Analytics: top creators by total points (public)
  const getCreatorPointsTop = async (n = 20) => {
    return await fetch(`${BASE_API_URL}/analytics/creator_points/top?n=${n}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  };

  // Analytics: project views history for a specific project
  const getProjectViewsHistory = async (
    projectName: string,
    start?: Date,
    end?: Date,
    granularity: 'minute' | 'hour' | 'day' | 'month' | 'year' = 'day',
    limit = 500
  ) => {
    const params = new URLSearchParams();
    if (start) {
      params.append('start', start.toISOString());
    }
    if (end) {
      params.append('end', end.toISOString());
    }
    params.append('granularity', granularity);
    params.append('limit', limit.toString());

    const url = `${BASE_API_URL}/analytics/project_views/history/${encodeURIComponent(
      projectName
    )}?${params.toString()}`;

    return await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  };

  // Analytics: top projects by views (public)
  const getProjectViewsTop = async (n = 20) => {
    return await fetch(`${BASE_API_URL}/analytics/project_views/top?n=${n}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  };

  // Sends a POST request to update the source code of a project file.
  const updateProjectFile = async (
    filePath: string,
    projectName: string,
    sourceCode: string,
    abortSignal?: AbortSignal
  ) => {
    const request = {
      file_path: filePath,
      project_name: projectName,
      source_code: sourceCode,
    };

    return await fetch(`${BASE_API_URL}/update_project_file`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
      signal: abortSignal,
    });
  };

  const updateProjectFiles = async (
    files: {
      projectName: string;
      filePath: string;
      fileContent: string;
    }[],
    abortSignal?: AbortSignal
  ) => {
    const request = files.map((file) => {
      return {
        project_name: file.projectName,
        file_path: file.filePath,
        source_code: file.fileContent,
      };
    });

    return await fetch(`${BASE_API_URL}/update_project_files`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
      signal: abortSignal,
    });
  };

  const preflightCheck = async (projectName?: string) => {
    const params = new URLSearchParams();
    if (projectName) params.append('project_name', projectName);
    const qs = params.toString();
    const url = `${BASE_API_URL}/preflight-check${qs ? `?${qs}` : ''}`;
    return await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
  };

  const getSEOMetadata = async (projectName: string) => {
    return await fetch(`${BASE_API_URL}/seo/metadata/${encodeURIComponent(projectName)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  };

  const updateSEOMetadata = async (
    projectName: string,
    data: {
      title?: string | null;
      description?: string | null;
      robots?: string | null;
      ga4_measurement_id?: string | null;
      favicon_href?: string | null;
      lang?: string | null;
    }
  ) => {
    return await fetch(`${BASE_API_URL}/seo/metadata/${encodeURIComponent(projectName)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  };

  const uploadFavicon = async (projectName: string, file: File) => {
    // Read file as ArrayBuffer to send as raw body
    const arrayBuffer = await file.arrayBuffer();

    // Always use image/x-icon since we only accept ICO files
    return await fetch(`${BASE_API_URL}/seo/favicon/${encodeURIComponent(projectName)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'image/x-icon',
      },
      body: arrayBuffer,
    });
  };

  const getFavicon = async (projectName: string) => {
    return await fetch(`${BASE_API_URL}/seo/favicon/${encodeURIComponent(projectName)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  };

  const deleteFavicon = async (projectName: string) => {
    return await fetch(`${BASE_API_URL}/seo/favicon/${encodeURIComponent(projectName)}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  };

  const undoProject = async (projectName: string) => {
    const request = {
      project_name: projectName,
    };
    return await fetch(`${BASE_API_URL}/undo_project`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  };

  const redoProject = async (projectName: string) => {
    const request = {
      project_name: projectName,
    };
    return await fetch(`${BASE_API_URL}/redo_project`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  };

  return {
    createProject,
    createBlankProject,
    getProjects,
    getProjectMetadata,
    getSubdomains,
    allocateSubdomain,
    updateSubdomain,
    releaseSubdomain,
    publishProject,
    unpublishProject,
    tagCommit,
    getComponentLibrary,
    addProjectComponent,
    addProjectChart,
    updateProjectComponentLayoutMetadata,
    updateProjectComponentSourceCode,
    updateApplicationMetadata,
    updateApplicationSettings,
    updateProjectFile,
    updateProjectFiles,
    deleteProjectFile,
    resetProjectFile,
    deleteProject,
    requestErrorCorrection,
    addImage,
    editElement,
    getUserSpendAnalytics,
    addPage,
    editPage,
    deletePage,
    editPageLayout,
    removePageComponent,
    getCreatorPointsSum,
    getCreatorPointsTop,
    getProjectViewsHistory,
    getProjectViewsTop,
    preflightCheck,
    getSEOMetadata,
    updateSEOMetadata,
    uploadFavicon,
    getFavicon,
    deleteFavicon,
    undoProject,
    redoProject,
  };
};

export { APIHandler };
